/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-nested-ternary */
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  ForwardRefExoticComponent,
  RefAttributes,
} from 'react';
import {
  Button,
  Flex,
  Spinner,
  Text,
  SimpleGrid,
  Box,
  Card,
  CardBody,
  useDisclosure,
  HStack,
  IconButton,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Grid,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { createSearchParams, useSearchParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  DownloadSimple,
  FunnelSimple,
  Package,
  FileText,
  ShoppingCart,
  File,
  CheckCircle,
  WarningCircle,
  Buildings,
  type IconProps,
} from 'phosphor-react';
import { ChevronLeft, ChevronRight, Calendar, BarChart3 } from 'lucide-react';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';
import { FormBox } from '../../../../components/formBox';
import SelectInputForm from '../../../../components/inputs/forms/SelectInputForm';
import { IData, ISelect } from '../../../../types/shared';
import { ICompany, useCompanies } from '../../../../hooks/useCompanies';
import { IClient } from '../../../../hooks/useClients';
import { formatCNPJ, getFormattedLabelString } from '../../../../utils/FormatFunctions';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { PageBox } from '../../../../components/pageBox';
import { FilterDrawer } from '../../../../components/FilterDrawer';
import { api } from '../../../../services/api';

moment.locale('pt-br');

interface DateRangeNavigatorProps {
  startDate: moment.Moment;
  endDate: moment.Moment;
  // eslint-disable-next-line no-unused-vars
  onSelectRange: (start: moment.Moment, end: moment.Moment) => void;
}

function DateRangeNavigator({ startDate, endDate, onSelectRange }: DateRangeNavigatorProps) {
  const isSameDay = startDate.isSame(endDate, 'day');
  const formattedRange = isSameDay
    ? startDate.format('DD [de] MMMM [de] YYYY')
    : `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;

  const [selectedMonth, setSelectedMonth] = useState(startDate.month());
  const [selectedYear, setSelectedYear] = useState(startDate.year());
  const [rangeStart, setRangeStart] = useState<moment.Moment | null>(null);
  const [rangeEnd, setRangeEnd] = useState<moment.Moment | null>(null);
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);

  const currentYear = moment().year();
  const currentMonth = moment().month();
  const today = moment();

  const handleMonthChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(prev => prev - 1);
      } else {
        setSelectedMonth(prev => prev - 1);
      }
    } else {
      const nextMonth = selectedMonth === 11 ? 0 : selectedMonth + 1;
      const nextYear = selectedMonth === 11 ? selectedYear + 1 : selectedYear;
      if (nextYear < currentYear || (nextYear === currentYear && nextMonth <= currentMonth)) {
        setSelectedMonth(nextMonth);
        if (selectedMonth === 11) {
          setSelectedYear(prev => prev + 1);
        }
      }
    }
  };

  const getDaysInMonth = () => {
    const daysInMonth = moment().year(selectedYear).month(selectedMonth).daysInMonth();
    const days: number[] = [];
    for (let i = 1; i <= daysInMonth; i += 1) {
      days.push(i);
    }
    return days;
  };

  const isDayDisabled = (day: number) => {
    const date = moment().year(selectedYear).month(selectedMonth).date(day);
    return date.isAfter(today, 'day');
  };

  const getDayStatus = (day: number) => {
    const date = moment().year(selectedYear).month(selectedMonth).date(day);

    // Durante seleção
    if (rangeStart && !rangeEnd) {
      if (date.isSame(rangeStart, 'day')) return 'start';
      return 'none';
    }

    if (rangeStart && rangeEnd) {
      if (date.isSame(rangeStart, 'day')) return 'start';
      if (date.isSame(rangeEnd, 'day')) return 'end';
      if (date.isAfter(rangeStart, 'day') && date.isBefore(rangeEnd, 'day')) return 'in-range';
      return 'none';
    }

    // Mostrar seleção atual dos filtros
    if (date.isSame(startDate, 'day') && date.isSame(endDate, 'day')) return 'single';
    if (date.isSame(startDate, 'day')) return 'start';
    if (date.isSame(endDate, 'day')) return 'end';
    if (date.isAfter(startDate, 'day') && date.isBefore(endDate, 'day')) return 'in-range';

    return 'none';
  };

  const handleDayClick = (day: number) => {
    const clickedDate = moment().year(selectedYear).month(selectedMonth).date(day);

    if (!isSelectingEnd) {
      // Primeiro clique - define o início
      setRangeStart(clickedDate);
      setRangeEnd(null);
      setIsSelectingEnd(true);
    } else if (rangeStart) {
      // Segundo clique - define o fim
      let finalStart = rangeStart;
      let finalEnd = clickedDate;

      // Se a data final for antes da inicial, inverte
      if (clickedDate.isBefore(rangeStart, 'day')) {
        finalStart = clickedDate;
        finalEnd = rangeStart;
      }

      setRangeEnd(finalEnd);
      onSelectRange(finalStart, finalEnd);
      setRangeStart(null);
      setRangeEnd(null);
      setIsSelectingEnd(false);
    }
  };

  const handleQuickSelect = (type: 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth') => {
    let start: moment.Moment;
    let end: moment.Moment;

    switch (type) {
      case 'today':
        start = moment();
        end = moment();
        break;
      case 'yesterday':
        start = moment().subtract(1, 'day');
        end = moment().subtract(1, 'day');
        break;
      case 'last7':
        start = moment().subtract(6, 'days');
        end = moment();
        break;
      case 'last30':
        start = moment().subtract(29, 'days');
        end = moment();
        break;
      case 'thisMonth':
        start = moment().startOf('month');
        end = moment();
        break;
      default:
        return;
    }

    onSelectRange(start, end);
    setRangeStart(null);
    setRangeEnd(null);
    setIsSelectingEnd(false);
  };

  const monthNames = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];

  const getDayStyle = (status: string) => {
    switch (status) {
      case 'start':
      case 'end':
      case 'single':
        return { variant: 'solid' as const, colorScheme: 'blue' };
      case 'in-range':
        return { variant: 'ghost' as const, colorScheme: 'blue', bg: 'blue.50' };
      default:
        return { variant: 'ghost' as const, colorScheme: 'gray' };
    }
  };

  return (
    <HStack spacing={2} bg="white" px={4} borderRadius="lg" shadow="sm" height="38px">
      <Popover
        placement="bottom"
        onClose={() => {
          setRangeStart(null);
          setRangeEnd(null);
          setIsSelectingEnd(false);
        }}
      >
        <PopoverTrigger>
          <HStack
            spacing={2}
            minW="280px"
            justify="center"
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
            px={3}
            py={2}
            borderRadius="md"
          >
            <Icon as={Calendar} color="gray.500" boxSize={4} />
            <Text fontWeight="medium" fontSize="sm" color="gray.700">
              {formattedRange}
            </Text>
          </HStack>
        </PopoverTrigger>
        <PopoverContent width="380px">
          <PopoverBody p={4}>
            {/* Botões de seleção rápida */}
            <HStack spacing={2} mb={4} flexWrap="wrap">
              <Button size="xs" variant="outline" onClick={() => handleQuickSelect('today')}>
                Hoje
              </Button>
              <Button size="xs" variant="outline" onClick={() => handleQuickSelect('yesterday')}>
                Ontem
              </Button>
              <Button size="xs" variant="outline" onClick={() => handleQuickSelect('last7')}>
                Últimos 7 dias
              </Button>
              <Button size="xs" variant="outline" onClick={() => handleQuickSelect('last30')}>
                Últimos 30 dias
              </Button>
              <Button size="xs" variant="outline" onClick={() => handleQuickSelect('thisMonth')}>
                Este mês
              </Button>
            </HStack>

            {/* Instrução de seleção */}
            <Text fontSize="xs" color="gray.500" mb={3} textAlign="center">
              {isSelectingEnd ? 'Selecione a data final' : 'Clique para selecionar a data inicial'}
            </Text>

            {/* Seletor de Mês/Ano */}
            <HStack justify="space-between" mb={4}>
              <IconButton
                aria-label="Mês anterior"
                icon={<Icon as={ChevronLeft} boxSize={4} />}
                onClick={() => handleMonthChange('prev')}
                variant="ghost"
                size="sm"
              />
              <Text fontWeight="bold" fontSize="md">
                {monthNames[selectedMonth]} {selectedYear}
              </Text>
              <IconButton
                aria-label="Próximo mês"
                icon={<Icon as={ChevronRight} boxSize={4} />}
                onClick={() => handleMonthChange('next')}
                variant="ghost"
                size="sm"
                isDisabled={selectedYear === currentYear && selectedMonth >= currentMonth}
              />
            </HStack>

            {/* Grid de Dias */}
            <Grid templateColumns="repeat(7, 1fr)" gap={1}>
              {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, idx) => (
                <Text
                  // eslint-disable-next-line react/no-array-index-key
                  key={`header-${idx}`}
                  fontSize="xs"
                  fontWeight="bold"
                  textAlign="center"
                  color="gray.500"
                  py={1}
                >
                  {day}
                </Text>
              ))}
              {/* Espaços vazios para alinhar o primeiro dia */}
              {Array.from({
                length: moment().year(selectedYear).month(selectedMonth).startOf('month').day(),
              }).map((_, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box key={`empty-${idx}`} />
              ))}
              {getDaysInMonth().map(day => {
                const status = getDayStatus(day);
                const style = getDayStyle(status);
                return (
                  <Button
                    key={day}
                    size="sm"
                    variant={style.variant}
                    colorScheme={style.colorScheme}
                    bg={status === 'in-range' ? 'blue.50' : undefined}
                    isDisabled={isDayDisabled(day)}
                    onClick={() => handleDayClick(day)}
                    fontSize="xs"
                    h="32px"
                    w="32px"
                    p={0}
                    minW="32px"
                  >
                    {day}
                  </Button>
                );
              })}
            </Grid>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </HStack>
  );
}

export function ModulesHomeIntegrationDailyImportsPage() {
  const navigate = useNavigate();
  const { getCompanies, getCompaniesFilters, getSaidasImportsTotais } = useCompanies();
  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onClose: onFilterDrawerClose,
  } = useDisclosure();
  const [searchMode, setSearchMode] = useState<'ID' | 'Nome' | 'CNPJ'>('ID');
  const searchModeRef = useRef(searchMode);

  // Ref para o observer do scroll infinito
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  // Importações diárias usa data única (mesmo dia para start e end)
  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      size: '20',
      search: '',
      date_start: moment().format('YYYY-MM-DD'),
      date_end: moment().format('YYYY-MM-DD'),
      screen_data: 'xml_quantities',
    }),
  );

  const filtersFormMethods = useForm<{
    search: string;
    cnpj: string;
    companies: ICompany;
    order_by: ISelect<string>;
    cliente: IClient;
    cidade: ISelect<string>;
    uf: ISelect<string>;
    grupo_de_empresas: ISelect<string>;
    tags: ISelect<string>;
    comercial: ISelect<string>;
    contabil: ISelect<string>;
    financeiro: ISelect<string>;
    contas_a_pagar: ISelect<string>;
    encerramento_anual: ISelect<string>;
    fiscal: ISelect<string>;
    imposto_de_renda: ISelect<string>;
    integracao_contabil: ISelect<string>;
    integracao_fiscal: ISelect<string>;
    pessoal: ISelect<string>;
    relacionamento_com_cliente: ISelect<string>;
    societario: ISelect<string>;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? '',
      cnpj: filters.get('cnpj') ?? '',
    },
  });

  // Debounce para o campo de busca
  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    const newFilters = new URLSearchParams(filters);
    const currentMode = searchModeRef.current;

    // Remove todos os parâmetros de busca anteriores
    newFilters.delete('search');
    newFilters.delete('cnpj');
    newFilters.delete('external_id');

    if (searchValue) {
      switch (currentMode) {
        case 'ID':
          newFilters.set('external_id', searchValue);
          break;
        case 'Nome':
          newFilters.set('search', searchValue);
          break;
        case 'CNPJ':
          newFilters.set('cnpj', searchValue.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
          break;
        default:
          break;
      }
    }

    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  // Re-executa busca quando muda o modo
  useEffect(() => {
    debouncedSearch(filtersFormMethods.getValues().search || '');
  }, [searchMode]);

  const debouncedOrderBy = useDebounceCallback((orderByValue: ISelect<string> | null) => {
    const newFilters = new URLSearchParams(filters);
    if (orderByValue?.value) {
      const ordem = orderByValue.value;
      if (ordem.startsWith('Maior')) {
        newFilters.set('order', 'desc');
        newFilters.set('order_by', ordem.replace('Maior ', '').toLowerCase());
      } else if (ordem.startsWith('Menor')) {
        newFilters.set('order', 'asc');
        newFilters.set('order_by', ordem.replace('Menor ', '').toLowerCase());
      }
    } else {
      newFilters.delete('order');
      newFilters.delete('order_by');
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') {
        debouncedSearch(values.search || '');
      } else if (name === 'order_by') {
        debouncedOrderBy((values.order_by as ISelect<string>) || null);
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch, debouncedSearch, debouncedOrderBy]);

  // Obter as datas do filtro como objeto moment
  const filterStartDate = moment(filters.get('date_start') ?? moment().format('YYYY-MM-DD'));
  const filterEndDate = moment(filters.get('date_end') ?? moment().format('YYYY-MM-DD'));

  // Função para selecionar intervalo de datas
  const handleSelectDateRange = (start: moment.Moment, end: moment.Moment) => {
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: start.format('YYYY-MM-DD'),
        date_end: end.format('YYYY-MM-DD'),
      }),
    );
  };

  // Função para aplicar os filtros avançados quando clica no botão "Filtrar"
  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    const newFilters = new URLSearchParams();

    // Mantém filtros básicos
    newFilters.set('page', '1');
    newFilters.set('size', '20');
    newFilters.set('search', filters.get('search') || '');
    if (filters.get('cnpj')) newFilters.set('cnpj', filters.get('cnpj') || '');
    newFilters.set('screen_data', 'xml_quantities');
    newFilters.set('date_start', filters.get('date_start') || moment().format('YYYY-MM-DD'));
    newFilters.set('date_end', filters.get('date_end') || moment().format('YYYY-MM-DD'));

    // Mantém order se existir
    const order = filters.get('order');
    const orderBy = filters.get('order_by');
    if (order) newFilters.set('order', order);
    if (orderBy) newFilters.set('order_by', orderBy);

    // Mantém note_type e has_records se existirem
    const noteType = filters.get('note_type');
    const hasRecords = filters.get('has_records');
    if (noteType) newFilters.set('note_type', noteType);
    if (hasRecords) newFilters.set('has_records', hasRecords);

    // Aplica filtros avançados
    if (values.companies?.external_id) newFilters.set('id', values.companies.external_id);
    if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
    if (values.uf?.value) newFilters.set('uf', values.uf.value);
    if (values.grupo_de_empresas?.value)
      newFilters.set('grupo_de_empresas', values.grupo_de_empresas.value);
    if (values.tags?.value) newFilters.set('tags', values.tags.value);
    if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
    if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
    if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
    if (values.contas_a_pagar?.value) newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
    if (values.encerramento_anual?.value)
      newFilters.set('encerramento_anual', values.encerramento_anual.value);
    if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
    if (values.imposto_de_renda?.value)
      newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
    if (values.integracao_contabil?.value)
      newFilters.set('integracao_contabil', values.integracao_contabil.value);
    if (values.integracao_fiscal?.value)
      newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
    if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
    if (values.relacionamento_com_cliente?.value)
      newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
    if (values.societario?.value) newFilters.set('societario', values.societario.value);

    setFilters(newFilters);
  };

  const queryKeyCompaniesFilters = ['CompaniesFilters'];
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: queryKeyCompaniesFilters,
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  useQuery<IData<ICompany[]>>({
    queryKey: ['CompaniesSelect', { page: 1, size: 30, search: '' }],
    queryFn: getCompanies,
  });

  const queryKey = [
    'CompaniesList-dailyImports',
    cleanFilters(Object.fromEntries([...Array.from(filters)])),
  ];

  const fetchCompanies = async ({ pageParam = 1 }) => {
    const filterParams = cleanFilters(Object.fromEntries([...Array.from(filters)]));
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
      params: {
        ...filterParams,
        page: pageParam,
        status_filter: 'ativas',
        filter_type: 'ativas_periodo',
      },
    });
    return res.data;
  };

  const {
    data,
    isFetching: isFetchingCompanyList,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: fetchCompanies,
    initialPageParam: 1,
    getNextPageParam: lastPage => lastPage.data.next_page,
  });

  // Combina todas as páginas em uma única lista, removendo duplicatas pelo ID
  const allCompanies = useMemo(() => {
    if (!data?.pages) return [];
    const seenIds = new Set<number>();
    const companiesData: ICompany[] = [];
    data.pages.forEach(page => {
      page.data.items.forEach(company => {
        if (!seenIds.has(company.id)) {
          seenIds.add(company.id);
          companiesData.push(company);
        }
      });
    });
    return companiesData;
  }, [data?.pages]);

  const totalCompanies = data?.pages?.[0]?.data?.total ?? 0;

  // Filtros base sem has_records e note_type para buscar totais de empresas
  const baseFiltersForCounts = useMemo(() => {
    const baseFilters = cleanFilters(Object.fromEntries([...Array.from(filters)]));
    delete baseFilters.has_records;
    delete baseFilters.note_type;
    delete baseFilters.page;
    baseFilters.size = '1'; // Só precisamos do total, não dos dados
    baseFilters.status_filter = 'ativas';
    baseFilters.filter_type = 'ativas_periodo';
    return baseFilters;
  }, [filters]);

  // Query para total de empresas (sem filtro has_records)
  const { data: totalEmpresasData } = useQuery({
    queryKey: ['CompaniesCount-total', baseFiltersForCounts],
    queryFn: async () => {
      const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
        params: baseFiltersForCounts,
      });
      return res.data.data.total;
    },
  });

  // Query para total de empresas COM registros
  const { data: totalComRegistrosData } = useQuery({
    queryKey: ['CompaniesCount-with', { ...baseFiltersForCounts, has_records: 'with' }],
    queryFn: async () => {
      const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
        params: { ...baseFiltersForCounts, has_records: 'with' },
      });
      return res.data.data.total;
    },
  });

  // Query para total de empresas SEM registros
  const { data: totalSemRegistrosData } = useQuery({
    queryKey: ['CompaniesCount-without', { ...baseFiltersForCounts, has_records: 'without' }],
    queryFn: async () => {
      const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
        params: { ...baseFiltersForCounts, has_records: 'without' },
      });
      return res.data.data.total;
    },
  });

  // Configura o IntersectionObserver para scroll infinito
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  // Criar filtros para totais sem note_type, has_records, size e page (para sempre mostrar valores globais nos cards)
  const filtersForTotals = useMemo(() => {
    const totalsFilters = new URLSearchParams(Object.fromEntries(filters));
    totalsFilters.delete('note_type'); // Remove filtro de tipo de nota dos totais
    totalsFilters.delete('has_records'); // Remove filtro de registros dos totais
    totalsFilters.delete('size'); // Remove paginação dos totais
    totalsFilters.delete('page'); // Remove paginação dos totais
    return cleanFilters(Object.fromEntries([...Array.from(totalsFilters)]));
  }, [filters]);

  const queryKeyTotais = ['getTotais-dailyImports', filtersForTotals];

  const { data: totais, isFetching: isFetchingTotais } = useQuery<any>({
    queryKey: queryKeyTotais,
    queryFn: getSaidasImportsTotais,
  });

  const totalCompaniesInScope = totalEmpresasData ?? 0;
  const percentWithRecords =
    totalCompaniesInScope > 0 ? ((totalComRegistrosData ?? 0) / totalCompaniesInScope) * 100 : 0;
  const percentWithoutRecords =
    totalCompaniesInScope > 0 ? ((totalSemRegistrosData ?? 0) / totalCompaniesInScope) * 100 : 0;

  const totalNotesInScope =
    (totais?.quantity_cte ?? 0) +
    (totais?.quantity_nfe ?? 0) +
    (totais?.quantity_nfce ?? 0) +
    (totais?.quantity_nfse ?? 0);
  const percentCte =
    totalNotesInScope > 0 ? ((totais?.quantity_cte ?? 0) / totalNotesInScope) * 100 : 0;
  const percentNfe =
    totalNotesInScope > 0 ? ((totais?.quantity_nfe ?? 0) / totalNotesInScope) * 100 : 0;
  const percentNfce =
    totalNotesInScope > 0 ? ((totais?.quantity_nfce ?? 0) / totalNotesInScope) * 100 : 0;
  const percentNfse =
    totalNotesInScope > 0 ? ((totais?.quantity_nfse ?? 0) / totalNotesInScope) * 100 : 0;

  const hasRecordsFilter = filters.get('has_records');
  const noteTypeFilter = filters.get('note_type');

  // Configuração de cores e ícones para cada tipo de card
  const cardConfig = {
    cte: {
      icon: Package,
      iconColor: '#1E40AF',
      borderColor: '#3B82F6',
    },
    nfe: {
      icon: FileText,
      iconColor: '#219653',
      borderColor: '#4CAF50',
    },
    nfce: {
      icon: ShoppingCart,
      iconColor: '#F97316',
      borderColor: '#FF9800',
    },
    nfse: {
      icon: File,
      iconColor: '#9333EA',
      borderColor: '#9C27B0',
    },
    withRecords: {
      icon: CheckCircle,
      iconColor: '#10B981',
      borderColor: '#22C55E',
    },
    withoutRecords: {
      icon: WarningCircle,
      iconColor: '#F59E0B',
      borderColor: '#FBBF24',
    },
    onlyFatura: {
      icon: FileText,
      iconColor: '#DC2626',
      borderColor: '#EF4444',
    },
    totalCompanies: {
      icon: Buildings,
      iconColor: '#6B7280',
      borderColor: '#9CA3AF',
    },
  };

  // Calcular filtros ativos para indicadores visuais (excluindo search e cnpj)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.get('id')) count += 1;
    if (filters.get('cidade')) count += 1;
    if (filters.get('uf')) count += 1;
    if (filters.get('grupo_de_empresas')) count += 1;
    if (filters.get('tags')) count += 1;
    if (filters.get('comercial')) count += 1;
    if (filters.get('contabil')) count += 1;
    if (filters.get('financeiro')) count += 1;
    if (filters.get('contas_a_pagar')) count += 1;
    if (filters.get('encerramento_anual')) count += 1;
    if (filters.get('fiscal')) count += 1;
    if (filters.get('imposto_de_renda')) count += 1;
    if (filters.get('integracao_contabil')) count += 1;
    if (filters.get('integracao_fiscal')) count += 1;
    if (filters.get('pessoal')) count += 1;
    if (filters.get('relacionamento_com_cliente')) count += 1;
    if (filters.get('societario')) count += 1;
    if (filters.get('order_by')) count += 1;
    return count;
  }, [filters]);

  const hasActiveFilters = activeFiltersCount > 0;

  const handleClearFilters = () => {
    const clearedFilters = createSearchParams({
      size: '20',
      search: filters.get('search') || '',
      date_start: filters.get('date_start') || moment().format('YYYY-MM-DD'),
      date_end: filters.get('date_end') || moment().format('YYYY-MM-DD'),
      screen_data: 'xml_quantities',
    });
    // Mantém order, cnpj, note_type e has_records se existirem
    const order = filters.get('order');
    const orderBy = filters.get('order_by');
    const cnpj = filters.get('cnpj');
    if (order) clearedFilters.set('order', order);
    if (orderBy) clearedFilters.set('order_by', orderBy);
    if (cnpj) clearedFilters.set('cnpj', cnpj);

    // Mantém note_type e has_records se existirem
    const noteType = filters.get('note_type');
    const hasRecords = filters.get('has_records');
    if (noteType) clearedFilters.set('note_type', noteType);
    if (hasRecords) clearedFilters.set('has_records', hasRecords);

    setFilters(clearedFilters);

    // Limpar cada campo individualmente para forçar atualização visual
    const { setValue } = filtersFormMethods;
    setValue('companies', null as any);
    setValue('cidade', null as any);
    setValue('uf', null as any);
    setValue('grupo_de_empresas', null as any);
    setValue('tags', null as any);
    setValue('comercial', null as any);
    setValue('contabil', null as any);
    setValue('financeiro', null as any);
    setValue('contas_a_pagar', null as any);
    setValue('encerramento_anual', null as any);
    setValue('fiscal', null as any);
    setValue('imposto_de_renda', null as any);
    setValue('integracao_contabil', null as any);
    setValue('integracao_fiscal', null as any);
    setValue('pessoal', null as any);
    setValue('relacionamento_com_cliente', null as any);
    setValue('societario', null as any);
  };

  // Função auxiliar para adicionar transparência à cor
  const addAlphaToColor = (color: string, alpha: number): string => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Componente para card de total
  const TotalCard = ({
    label,
    value,
    percent,
    isActive,
    onClick,
    icon: IconComponent,
    iconColor,
    borderColor,
  }: {
    label: string;
    value: number;
    percent?: string;
    isActive?: boolean;
    onClick?: () => void;
    icon?: ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;
    iconColor?: string;
    borderColor?: string;
  }) => {
    const finalBorderColor = isActive ? borderColor || iconColor || '#0077B6' : 'border';
    const finalBorderWidth = isActive ? '2px solid' : '1px solid';
    const iconBgColor = iconColor ? addAlphaToColor(iconColor, 0.15) : 'transparent';

    return (
      <Card
        as={onClick ? 'button' : 'div'}
        onClick={onClick}
        cursor={onClick ? 'pointer' : 'default'}
        bg="body"
        border={finalBorderWidth}
        borderColor={finalBorderColor}
        borderRadius="16px"
        transition="all 0.2s"
        _hover={onClick ? { transform: 'translateY(-2px)', shadow: 'md' } : {}}
        h="full"
        position="relative"
        overflow="hidden"
      >
        <CardBody p={{ base: 3, md: 4 }}>
          <Flex flexDirection="column" gap={1.5}>
            {IconComponent && iconColor && (
              <Flex alignItems="center" justifyContent="flex-start" mb={0.5} gap={2}>
                <Box
                  p={1.5}
                  borderRadius="md"
                  bg={iconBgColor}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <IconComponent size={18} color={iconColor} weight="duotone" />
                </Box>
                <Text fontSize={{ base: '11px', md: '12px' }} color="#718EBF" fontWeight="medium">
                  {label}
                </Text>
              </Flex>
            )}
            {!IconComponent && (
              <Text fontSize={{ base: '11px', md: '12px' }} color="#718EBF">
                {label}
              </Text>
            )}
            <Text fontSize={{ base: '14px', md: '16px' }} fontWeight="bold" color="#1A202C">
              {value}
            </Text>
            {percent && (
              <Text fontSize={{ base: '9px', md: '11px' }} color="#94A3B8">
                {percent}
              </Text>
            )}
          </Flex>
        </CardBody>
      </Card>
    );
  };

  return (
    <PageBox>
      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        {/* Header */}
        <Flex
          flexDirection={{ base: 'column', lg: 'row' }}
          alignItems={{ base: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
          gap={4}
          mb={6}
        >
          <Flex alignItems="center" gap={3}>
            <ReturnButton previousPage="modulesHome/integration" />
            <Flex
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              bg="#D9F1F1"
              p={3}
            >
              <DownloadSimple color="#0077B6" size={32} />
            </Flex>
            <Text fontWeight="bold" fontSize={{ base: '18px', md: '20px' }}>
              Importações Diárias
            </Text>
          </Flex>
          <Button
            leftIcon={<Icon as={BarChart3} boxSize={4} />}
            colorScheme="blue"
            variant="outline"
            size="sm"
            onClick={() => {
              const dateStart = filters.get('date_start') || moment().format('YYYY-MM-DD');
              const selectedMoment = moment(dateStart);
              navigate({
                pathname: '/modulesHome/integration/importsDashboard',
                search: createSearchParams({
                  date_start: selectedMoment.startOf('month').format('YYYY-MM-DD'),
                  date_end: selectedMoment.endOf('month').format('YYYY-MM-DD'),
                }).toString(),
              });
            }}
          >
            Dashboard de Importações
          </Button>
        </Flex>

        {/* Cards de Totais */}
        {isFetchingTotais ? (
          <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={4} py={8}>
            <Spinner size="lg" color="primary" />
            <Text fontSize="xl" fontWeight="semibold">
              Carregando...
            </Text>
          </Flex>
        ) : (
          <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={{ base: 3, md: 4 }} mb={6}>
            <TotalCard
              label="Total CTE"
              value={totais?.quantity_cte ?? 0}
              percent={`${percentCte.toFixed(1)}%`}
              isActive={noteTypeFilter === 'cte'}
              icon={cardConfig.cte.icon}
              iconColor={cardConfig.cte.iconColor}
              borderColor={cardConfig.cte.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (noteTypeFilter === 'cte') {
                  newFilters.delete('note_type');
                } else {
                  newFilters.set('note_type', 'cte');
                  newFilters.delete('has_records'); // Remove filtro de registros
                }
                newFilters.set('page', '1');
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Total NFE"
              value={totais?.quantity_nfe ?? 0}
              percent={`${percentNfe.toFixed(1)}%`}
              isActive={noteTypeFilter === 'nfe'}
              icon={cardConfig.nfe.icon}
              iconColor={cardConfig.nfe.iconColor}
              borderColor={cardConfig.nfe.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (noteTypeFilter === 'nfe') {
                  newFilters.delete('note_type');
                } else {
                  newFilters.set('note_type', 'nfe');
                  newFilters.delete('has_records'); // Remove filtro de registros
                }
                newFilters.set('page', '1');
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Total NFCE"
              value={totais?.quantity_nfce ?? 0}
              percent={`${percentNfce.toFixed(1)}%`}
              isActive={noteTypeFilter === 'nfce'}
              icon={cardConfig.nfce.icon}
              iconColor={cardConfig.nfce.iconColor}
              borderColor={cardConfig.nfce.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (noteTypeFilter === 'nfce') {
                  newFilters.delete('note_type');
                } else {
                  newFilters.set('note_type', 'nfce');
                  newFilters.delete('has_records'); // Remove filtro de registros
                }
                newFilters.set('page', '1');
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Total NFSE"
              value={totais?.quantity_nfse ?? 0}
              percent={`${percentNfse.toFixed(1)}%`}
              isActive={noteTypeFilter === 'nfse'}
              icon={cardConfig.nfse.icon}
              iconColor={cardConfig.nfse.iconColor}
              borderColor={cardConfig.nfse.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (noteTypeFilter === 'nfse') {
                  newFilters.delete('note_type');
                } else {
                  newFilters.set('note_type', 'nfse');
                  newFilters.delete('has_records'); // Remove filtro de registros
                }
                newFilters.set('page', '1');
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Empresas com registros"
              value={totalComRegistrosData ?? 0}
              percent={`${percentWithRecords.toFixed(1)}%`}
              isActive={hasRecordsFilter === 'with'}
              icon={cardConfig.withRecords.icon}
              iconColor={cardConfig.withRecords.iconColor}
              borderColor={cardConfig.withRecords.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (hasRecordsFilter === 'with') {
                  newFilters.delete('has_records');
                } else {
                  newFilters.set('has_records', 'with');
                  newFilters.delete('note_type'); // Remove filtro de tipo de nota
                }
                newFilters.set('page', '1');
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Empresas sem registros"
              value={totalSemRegistrosData ?? 0}
              percent={`${percentWithoutRecords.toFixed(1)}%`}
              isActive={hasRecordsFilter === 'without'}
              icon={cardConfig.withoutRecords.icon}
              iconColor={cardConfig.withoutRecords.iconColor}
              borderColor={cardConfig.withoutRecords.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (hasRecordsFilter === 'without') {
                  newFilters.delete('has_records');
                } else {
                  newFilters.set('has_records', 'without');
                  newFilters.delete('note_type'); // Remove filtro de tipo de nota
                }
                newFilters.set('page', '1');
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Empresas com Fatura"
              value={totais?.companies_only_fatura ?? 0}
              isActive={hasRecordsFilter === 'only_fatura'}
              icon={cardConfig.onlyFatura.icon}
              iconColor={cardConfig.onlyFatura.iconColor}
              borderColor={cardConfig.onlyFatura.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (hasRecordsFilter === 'only_fatura') {
                  newFilters.delete('has_records');
                } else {
                  newFilters.set('has_records', 'only_fatura');
                  newFilters.delete('note_type'); // Remove filtro de tipo de nota
                }
                newFilters.set('page', '1');
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Total de Empresas"
              value={totalEmpresasData ?? 0}
              isActive={!noteTypeFilter && !hasRecordsFilter}
              icon={cardConfig.totalCompanies.icon}
              iconColor={cardConfig.totalCompanies.iconColor}
              borderColor={cardConfig.totalCompanies.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                newFilters.delete('note_type');
                newFilters.delete('has_records');
                newFilters.set('page', '1');
                setFilters(newFilters);
              }}
            />
          </SimpleGrid>
        )}
        {/* Filtros e Controles */}
        <FormBox>
          <Box p={{ base: 4, md: 6 }}>
            <FormProvider {...filtersFormMethods}>
              {/* Barra de Busca e Botões */}
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align={{ base: 'stretch', md: 'center' }}
                gap={4}
                mb={4}
              >
                {/* Campos de Pesquisa */}
                <HStack spacing={2} flex={1} maxW={{ base: '100%', md: '500px' }}>
                  <SearchModeBar
                    modes={MODES_ID_NOME_CNPJ}
                    mode={searchMode}
                    onModeChange={m => setSearchMode(m as 'ID' | 'Nome' | 'CNPJ')}
                    value={filtersFormMethods.watch('search') ?? ''}
                    onValueChange={v =>
                      filtersFormMethods.setValue('search', v, { shouldDirty: true })
                    }
                  />
                </HStack>

                {/* Navegação de Dia, Ordenação e Filtros */}
                <HStack spacing={3} flexWrap="wrap">
                  <Box minW="180px">
                    <SelectInputForm<ISelect<string>>
                      name="order_by"
                      options={[
                        { label: 'Maior CTE', value: 'Maior CTE' },
                        { label: 'Menor CTE', value: 'Menor CTE' },
                        { label: 'Maior NFE', value: 'Maior NFE' },
                        { label: 'Menor NFE', value: 'Menor NFE' },
                        { label: 'Maior NFCE', value: 'Maior NFCE' },
                        { label: 'Menor NFCE', value: 'Menor NFCE' },
                        { label: 'Maior NFSE', value: 'Maior NFSE' },
                        { label: 'Menor NFSE', value: 'Menor NFSE' },
                        { label: 'Maior TOTAL', value: 'Maior TOTAL' },
                        { label: 'Menor TOTAL', value: 'Menor TOTAL' },
                      ]}
                      placeholder="Ordenar por"
                      controlMb={0}
                      isClearable
                      chakraStyles={{
                        control: styles => ({
                          ...styles,
                          minH: '38px',
                          height: '38px',
                        }),
                      }}
                    />
                  </Box>
                  <DateRangeNavigator
                    startDate={filterStartDate}
                    endDate={filterEndDate}
                    onSelectRange={handleSelectDateRange}
                  />
                  <Button
                    onClick={onFilterDrawerOpen}
                    leftIcon={<FunnelSimple size={16} />}
                    variant="outline"
                    colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                    height="38px"
                  >
                    Filtros {hasActiveFilters && `(${activeFiltersCount})`}
                  </Button>
                </HStack>
              </Flex>

              {/* FilterDrawer para filtros avançados */}
              <FilterDrawer
                isOpen={isFilterDrawerOpen}
                onClose={onFilterDrawerClose}
                title="Filtros Avançados"
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
                onApplyFilters={handleApplyFilters}
                formMethods={filtersFormMethods}
                size="lg"
              >
                <div className="grid grid-cols-2 gap-4">
                  <SelectInputForm<ISelect<string>>
                    name="cidade"
                    placeholder="Filtrar por cidade"
                    options={getFormattedLabelString(CompaniesFilters?.cidade)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="uf"
                    placeholder="Filtrar por Estado"
                    options={getFormattedLabelString(CompaniesFilters?.uf)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="grupo_de_empresas"
                    placeholder="Filtrar por grupo de empresa"
                    options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="tags"
                    placeholder="Filtrar por tags"
                    options={getFormattedLabelString(CompaniesFilters?.tags)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="comercial"
                    placeholder="Filtrar por comercial"
                    options={getFormattedLabelString(CompaniesFilters?.comercial)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="contabil"
                    placeholder="Filtrar pelo contábil"
                    options={getFormattedLabelString(CompaniesFilters?.contabil)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="financeiro"
                    placeholder="Filtrar pelo financeiro"
                    options={getFormattedLabelString(CompaniesFilters?.financeiro)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="contas_a_pagar"
                    placeholder="Filtrar por contas à pagar"
                    options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="encerramento_anual"
                    placeholder="Filtrar por encerramento anual"
                    options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="fiscal"
                    placeholder="Filtrar por fiscal"
                    options={getFormattedLabelString(CompaniesFilters?.fiscal)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="imposto_de_renda"
                    placeholder="filtrar por imposto de renda"
                    options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="integracao_contabil"
                    placeholder="Filtrar por integração contabil"
                    options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="integracao_fiscal"
                    placeholder="Filtrar por integração fiscal"
                    options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="pessoal"
                    placeholder="Filtrar por pessoal"
                    options={getFormattedLabelString(CompaniesFilters?.pessoal)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="relacionamento_com_cliente"
                    placeholder="Filtrar por relacionamento com cliente"
                    options={getFormattedLabelString(CompaniesFilters?.relacionamento_com_cliente)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="societario"
                    placeholder="Filtrar por societário"
                    options={getFormattedLabelString(CompaniesFilters?.societario)}
                    controlMb={0}
                  />
                </div>
              </FilterDrawer>
            </FormProvider>

            {/* Lista de Empresas */}
            {isFetchingCompanyList && allCompanies.length === 0 ? (
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={4}
                py={8}
              >
                <Spinner size="lg" color="primary" />
                <Text fontSize="xl" fontWeight="semibold">
                  Carregando...
                </Text>
              </Flex>
            ) : !isFetchingCompanyList && allCompanies.length === 0 ? (
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={4}
                py={8}
              >
                <Text fontSize="lg" color="gray.500">
                  Nenhum item encontrado
                </Text>
              </Flex>
            ) : (
              <Box>
                {totalCompanies > 0 && (
                  <Text mb={2} fontSize="sm" color="gray.600">
                    Exibindo {allCompanies.length} de {totalCompanies} empresas
                  </Text>
                )}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                  {allCompanies.map((item, index) => (
                    <Card key={item?.external_id || index} borderRadius="lg" shadow="md">
                      <CardBody p={{ base: 4, md: 6 }}>
                        {/* Dados da Empresa */}
                        <Box mb={4} pb={4} borderBottom="1px solid" borderColor="gray.200">
                          <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={3}>
                            Dados da Empresa
                          </Text>
                          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={1}>
                                ID
                              </Text>
                              <Text fontSize="sm" fontWeight="medium">
                                {item?.external_id}
                              </Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={1}>
                                CNPJ
                              </Text>
                              <Text fontSize="sm" fontWeight="medium">
                                {formatCNPJ(item?.cnpj)}
                              </Text>
                            </Box>
                            <Box gridColumn={{ base: '1', sm: 'span 3' }}>
                              <Text fontSize="xs" color="gray.500" mb={1}>
                                Razão Social
                              </Text>
                              <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                                {item?.razao_social}
                              </Text>
                            </Box>
                          </SimpleGrid>
                        </Box>

                        {/* Quantidades de Notas */}
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={3}>
                            Quantidades de Notas
                          </Text>
                          <SimpleGrid columns={{ base: 2, sm: 5 }} spacing={3}>
                            <Box textAlign={{ base: 'left', sm: 'center' }}>
                              <Text fontSize="xs" color="gray.500" mb={1}>
                                CTE
                              </Text>
                              <Text fontSize="md" fontWeight="bold" color="#0077B6">
                                {item.quantidade_cte ?? 0}
                              </Text>
                            </Box>
                            <Box textAlign={{ base: 'left', sm: 'center' }}>
                              <Text fontSize="xs" color="gray.500" mb={1}>
                                NFE
                              </Text>
                              <Text fontSize="md" fontWeight="bold" color="#0077B6">
                                {item.quantidade_nfe ?? 0}
                              </Text>
                            </Box>
                            <Box textAlign={{ base: 'left', sm: 'center' }}>
                              <Text fontSize="xs" color="gray.500" mb={1}>
                                NFCE
                              </Text>
                              <Text fontSize="md" fontWeight="bold" color="#0077B6">
                                {item.quantidade_nfce ?? 0}
                              </Text>
                            </Box>
                            <Box textAlign={{ base: 'left', sm: 'center' }}>
                              <Text fontSize="xs" color="gray.500" mb={1}>
                                NFSE
                              </Text>
                              <Text fontSize="md" fontWeight="bold" color="#0077B6">
                                {item.quantidade_nfse ?? 0}
                              </Text>
                            </Box>
                            <Box textAlign={{ base: 'left', sm: 'center' }}>
                              <Text fontSize="xs" color="gray.500" mb={1}>
                                TOTAL
                              </Text>
                              <Text fontSize="md" fontWeight="bold" color="#219653">
                                {item.quantidade_notas ?? 0}
                              </Text>
                            </Box>
                          </SimpleGrid>
                        </Box>
                      </CardBody>
                    </Card>
                  ))}
                </SimpleGrid>

                {/* Elemento observado para carregar mais */}
                <Box ref={lastElementRef} py={4} textAlign="center">
                  {isFetchingNextPage && (
                    <Flex alignItems="center" justifyContent="center" gap={2}>
                      <Spinner size="md" color="blue.500" />
                      <Text color="gray.500">Carregando mais empresas...</Text>
                    </Flex>
                  )}
                  {!hasNextPage && allCompanies.length > 0 && (
                    <Text color="gray.400" fontSize="sm">
                      Todas as empresas foram carregadas
                    </Text>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </FormBox>
      </Box>
    </PageBox>
  );
}
